import React from 'react';
import { Button } from '../../ui/button';
import { Input } from '../../ui/input';
import type { ApiKeyCardProps } from '../types';

export const ApiKeyCard: React.FC<ApiKeyCardProps> = ({
  title,
  description,
  placeholder,
  hasStoredKey,
  keyValue,
  showKey,
  statusColorClass,
  isLoading = false,
  onKeyChange,
  onToggleShow,
  onSave,
  onRemove,
  onTestConnection,
  issuePageUrl,
  issuePageLabel
}) => {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4">
      <div className="flex items-start justify-between gap-2 mb-3">
        <div>
          <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">{description}</p>
        </div>
        <span className={`inline-flex items-center rounded-full px-2 py-0.5 text-[11px] font-medium ${statusColorClass}`}>
          {hasStoredKey ? 'Configured' : 'Not configured'}
        </span>
      </div>

      {hasStoredKey ? (
        <div className="space-y-3">
          {showKey && keyValue && (
            <div className="rounded-md bg-gray-50 dark:bg-gray-900 p-2 text-xs text-gray-700 dark:text-gray-300 font-mono break-all">
              {keyValue}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <Button onClick={onToggleShow} variant="outline" size="sm">
              {showKey ? 'Hide Key' : 'Show Key'}
            </Button>
            {onTestConnection && (
              <Button onClick={onTestConnection} variant="outline" size="sm" disabled={isLoading}>
                {isLoading ? 'Testing...' : 'Test Connection'}
              </Button>
            )}
            <Button
              onClick={onRemove}
              variant="outline"
              size="sm"
              className="text-red-600 hover:text-red-700"
            >
              Remove
            </Button>
          </div>
        </div>
      ) : (
        <div className="space-y-3">
          <div className="flex gap-2">
            <Input
              type={showKey ? 'text' : 'password'}
              value={keyValue}
              onChange={(e) => onKeyChange(e.target.value)}
              placeholder={placeholder}
              className="font-mono"
            />
            <Button onClick={onToggleShow} variant="outline" size="sm" type="button">
              {showKey ? 'Hide' : 'Show'}
            </Button>
          </div>
          {issuePageUrl && issuePageLabel && (
            <a
              href={issuePageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block text-xs text-blue-600 dark:text-blue-400 hover:underline"
            >
              {issuePageLabel}
            </a>
          )}
          <Button
            onClick={onSave}
            disabled={!keyValue.trim() || isLoading}
            className="w-full sm:w-auto"
            size="sm"
          >
            {isLoading ? 'Saving...' : `Save ${title.replace(' API Key', '').replace(' (Standard)', '')} Key`}
          </Button>
        </div>
      )}
    </div>
  );
};
